import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FleetTabs, PageHeader, NewCampaignModal, Pill, StatusPill, ActivityPill, JOB_COLORS, fmtDate, ago, errText, useLiveRefresh } from './common';
import { JobOutputModal } from './FleetCampaignDetail';

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderTop: '1px solid var(--border)', fontSize: 13 }}>
      <div style={{ width: 210, color: 'var(--text-muted)', flexShrink: 0 }}>{label}</div>
      <div style={{ wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}

export default function FleetHostDetail() {
  const { cn } = useParams();
  const navigate = useNavigate();
  const [h, setH] = useState(null);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [jobId, setJobId] = useState(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`/cve-fleet/hosts/${encodeURIComponent(cn)}`); setH(r.data); setError(''); }
    catch (e) { setError(errText(e, 'Failed to load the server')); }
  }, [cn]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, 30000);

  async function op(name, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    try { await axios.post(`/cve-fleet/hosts/${encodeURIComponent(cn)}/${name}`); load(); }
    catch (e) { alert(errText(e, `Failed to ${name}`)); }
  }

  const rep = h?.report;
  return (
    <div>
      <PageHeader />
      <div className="page-body">
        <FleetTabs />
        <div style={{ marginBottom: 12 }}><Link to="/cve-fleet">← All servers</Link></div>
        {error && <div className="error-msg">{error}</div>}
        {!h ? <div className="spinner" /> : (
          <>
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0 }}>{h.cn}</h3>
                <StatusPill status={h.status} />
                {h.mitigated != null && <Pill label={h.mitigated ? 'mitigated' : 'not mitigated'} color={h.mitigated ? 'var(--success)' : 'var(--danger)'} />}
                {h.disabled && <Pill label="disabled" color="var(--danger)" />}
                {!h.enrolled && <Pill label="awaiting re-enroll" color="var(--warning)" />}
                <div className="spacer" style={{ flex: 1 }} />
                <button className="btn btn-primary" disabled={h.disabled || !h.enrolled} onClick={() => setShowNew(true)}>Run action…</button>
                {h.disabled
                  ? <button className="btn btn-secondary" onClick={() => op('enable')}>Enable</button>
                  : <button className="btn btn-secondary" onClick={() => op('disable', `Disable ${h.cn}? Its agent is refused and its pending jobs are cancelled.`)}>Disable</button>}
                <button className="btn btn-secondary" style={{ color: 'var(--danger)' }}
                        onClick={() => op('reset', `Reset ${h.cn}'s enrollment? Its current certificate stops working at once, and a server with this name can enroll again with a token.`)}>Reset enrollment</button>
              </div>
              <div style={{ marginTop: 12 }}>
                <Row label="Hostname">{h.hostname || '—'}</Row>
                <Row label="OS · kernel">{h.os || '—'} · {h.kernel || '—'}</Row>
                <Row label="Tags">{h.tags.join(', ') || '—'}</Row>
                <Row label="Agent">{h.agent_version || '—'} · last seen {ago(h.last_seen)} from {h.last_ip || '—'}</Row>
                <Row label="Last report">{fmtDate(h.report_at)}</Row>
                <Row label="Enrolled">{fmtDate(h.enrolled_at)} from {h.enrolled_ip || '—'} · certificate expires {fmtDate(h.cert_expires)}</Row>
                <Row label="Enrolled with token">{h.enroll_token ? <>{h.enroll_token.label || `token #${h.enroll_token.id}`} <code>{h.enroll_token.prefix}…</code></> : '—'}</Row>
              </div>
            </div>
            {rep && (
              <div className="card" style={{ marginBottom: 16 }}>
                <h4 style={{ marginTop: 0 }}>Detection report</h4>
                <Row label="Evidence">{rep.evidence}</Row>
                <Row label="Fixed kernels installed">{(rep.fixed_kernels_installed || []).join(', ') || 'none'}</Row>
                {rep.exposure && <>
                  <Row label="IPv4 TCP listeners (non-loopback)">{rep.exposure.ipv4_tcp_listeners}</Row>
                  <Row label="MPTCP enabled · listeners">{rep.exposure.mptcp_enabled} · {rep.exposure.mptcp_listeners}</Row>
                  <Row label="tcp_migrate_req · reuseport BPF">{rep.exposure.tcp_migrate_req} · {rep.exposure.sk_reuseport_bpf_progs}</Row>
                </>}
                {rep.mitigation && <>
                  <Row label="Drop rule active">{String(rep.mitigation.nft_rule_active)} (dropped packets: {rep.mitigation.nft_dropped_packets})</Row>
                  <Row label="Monitor rule matches">{rep.mitigation.monitor_matched_packets}</Row>
                </>}
                {rep.activity && <>
                  <Row label="Activity verdict"><ActivityPill activity={h.activity} /></Row>
                  <Row label="Kernel errors (7 d) · TCP-related">{rep.activity.kernel_errors_7d} · {rep.activity.tcp_related_kernel_errors_7d}</Row>
                  <Row label="Kernel crash dumps (7 d)">{rep.activity.kernel_crash_dumps_7d ?? 'not reported (agent before 1.2.0)'}</Row>
                  <Row label="Anything in /var/crash (7 d)">{rep.activity.crash_dumps_7d}</Row>
                  {(rep.activity.recent_kernel_errors || []).length > 0 &&
                    <Row label="Recent kernel errors"><pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>{rep.activity.recent_kernel_errors.join('\n')}</pre></Row>}
                </>}
                {rep.raw && <Row label="Raw output"><pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>{rep.raw}</pre></Row>}
              </div>
            )}
            <div className="card">
              <h4 style={{ marginTop: 0 }}>Recent jobs</h4>
              {h.recent_jobs.length === 0 ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No jobs yet.</div> : (
                <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-muted)' }}>
                    <th style={{ padding: 8 }}>Job</th><th style={{ padding: 8 }}>Campaign</th><th style={{ padding: 8 }}>Action</th>
                    <th style={{ padding: 8 }}>State</th><th style={{ padding: 8 }}>Exit</th><th style={{ padding: 8 }}>Finished</th></tr></thead>
                  <tbody>{h.recent_jobs.map(j => (
                    <tr key={j.id} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => setJobId(j.id)}>
                      <td style={{ padding: 8 }}>#{j.id}</td>
                      <td style={{ padding: 8 }}><Link to={`/cve-fleet/campaigns/${j.campaign_id}`} onClick={e => e.stopPropagation()}>#{j.campaign_id}</Link></td>
                      <td style={{ padding: 8 }}>{j.action}</td>
                      <td style={{ padding: 8 }}><Pill label={j.state} color={JOB_COLORS[j.state]} /></td>
                      <td style={{ padding: 8 }}>{j.exit_code ?? '—'}</td>
                      <td style={{ padding: 8 }}>{fmtDate(j.finished_at)}</td>
                    </tr>))}</tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
      {showNew && <NewCampaignModal preset={{ hosts: [cn] }} onClose={() => setShowNew(false)} onCreated={c => navigate(`/cve-fleet/campaigns/${c.id}`)} />}
      {jobId && <JobOutputModal jobId={jobId} onClose={() => setJobId(null)} />}
    </div>
  );
}
